<style scoped>
.invalid-feedbacks{
    color: #F64E60; 
}
</style>
<!--begin::Contact person modal component-->
<template>
    <div id="contact-person-modal" data-backdrop="static" data-keyboard="false" class="modal fade show contact-person-modal" tabindex="-1" role="dialog" aria-labelledby="contactPersonModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <Form @submit="saveContactPerson" :validation-schema="schema" v-slot="{errors}" ref="formRef" class="kt-form kt-form--label-right">
                    <div class="modal-header">
                        <h5 class="modal-title font-weight-bolder font-size-lg" id="contactPersonModalLabel" v-if="action_mode == 'add'"> Add Customer Contact</h5>
                        <h5 class="modal-title font-weight-bolder font-size-lg" id="contactPersonModalLabel" v-else-if="action_mode == 'edit'"> Edit Customer Contact</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click.prevent="formReset">
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="form-group row">
                            <div class="col-lg-6">
                                <label class="font-weight-bolder font-size-lg">Name*</label>
                                <Field type="text" name="contact_name" v-model="contact_name" placeholder="Enter name" class="form-control form-elements" :class="{ 'is-invalid': errors['contact_name'] }"/>
                                <ErrorMessage  name="contact_name" class="error-message"/>
                            </div>
                            <div class="col-lg-6">
                                <label class="font-weight-bolder font-size-lg">Email*</label>
                                <Field type="email" name="contact_email" v-model="contact_email" placeholder="Enter email" class="form-control form-elements" :class="{ 'is-invalid': errors['contact_email']}"/>
                                <ErrorMessage  name="contact_email" class="error-message"/>
                            </div>
                        </div>
                        <div class="form-group row">
                            <div class="col-lg-6">
                                <label class="font-weight-bolder font-size-lg">Username*</label>
                                <Field type="text" name="contact_username" v-model="contact_username" placeholder="Enter Username" class="form-control form-elements" :class="{ 'is-invalid': errors['contact_username']}"/>
                                <ErrorMessage  name="contact_username" class="error-message"/>
                                <div class="input-group">
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <label class="font-weight-bolder font-size-lg">Phone Number</label>
                                <Field type="text" name="contact_phone_number" v-model="contact_phone_number" placeholder="Enter phone number" class="form-control form-elements" :class="{ 'is-invalid': errors['contact_phone_number']}"/>
                                <ErrorMessage  name="contact_phone_number" class="error-message"/>
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="font-weight-bolder">Enable Customer Login*</label>
                            <div class="radio-inline">
                                <label class="radio">
                                    <input type="radio" name="contact_login_enabled" checked="checked" v-model="contact_login_enabled" value="1"><span></span> Yes
                                    </label> <label  class="radio"><input type="radio" name="contact_login_enabled" v-model="contact_login_enabled" value="0"><span></span>No
                                </label>
                            </div>
                            <span class="form-text text-muted" v-if="action_mode == 'add'">This customer will be notified via email that an account has been created in their name, and a secure password will be auto-generated and sent to them via email. They may login and use the wholesale portal normally, and you may also create orders on their behalf.</span>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal" @click.prevent="formReset">Close</button>
                        <button type="submit" :class="{'btn btn-primary': true, 'spinner spinner-white spinner-right disabled': spinner }">Save</button>
                    </div>
                </Form>
            </div>
        </div>
    </div>
</template>
<!--end::Contact person modal component-->

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import ContactPersonAPIService from '@/js/api/customers/contact-persons';
import {toast} from "vue3-toastify";
import 'vue3-toastify/dist/index.css';
import eventBus from '@/js/utils/eventBus';

const props = defineProps({
  customerId: String,
  userToken: String,
});

const contact_name = ref('');
const contact_email = ref('');
const contact_username = ref('');
const contact_phone_number = ref('');
const contact_login_enabled = ref(1);
const contact_type = ref('secondary');
const action_mode = ref('add');
const contact_person_id = ref('');
const spinner = ref(false);
const buttonDisable = ref(false);
const formRef = ref(null);


const schema = computed(() => {
  return yup.object({
    contact_name: yup.string().required('Name is required').min(3, 'Contact Name must be at least 3 characters').max(100, 'Name must be at most 100 characters'),
    contact_email: yup.string().required('Email is required').email('Email is not valid'),
    contact_username: yup
      .string()
      .required('User Name is required').min(3, 'User Name must be at least 3 characters').max(30, 'User Name must be at most 30 characters')
      .matches(/^[a-zA-Z0-9]*$/, 'User Name can only contain alphanumeric characters'),
  });
});

const isEdit = computed(() => {
  if (action_mode.value === 'edit') {
    formReset();
    return true;
  }
  return false;
});

const formReset = () => {
  contact_name.value = '';
  contact_email.value = '';
  contact_username.value = '';
  contact_phone_number.value = '';
  contact_login_enabled.value = 1;
  contact_type.value = 'secondary';

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const saveContactPerson = async () => {
  spinner.value = true;
  const data = {
    name: contact_name.value,
    email: contact_email.value,
    username: contact_username.value,
    phone_number: contact_phone_number.value,
    login_enabled: contact_login_enabled.value,
    contact_type: contact_type.value,
  };

  try {
    let response;
    if (action_mode.value === 'add') {
      response = await ContactPersonAPIService.createContactPerson(data, props.customerId, props.userToken);
    } else {
      response = await ContactPersonAPIService.updateContactPerson(data, contact_person_id.value, props.customerId, props.userToken);
    }

    if (response.data.success) {
      $('#contact-person-modal').modal('hide');
      formReset();
      spinner.value = false;
      buttonDisable.value = false;
      toast.success('Success! '+response.data.success);
      eventBus.emit('contactPersonSaveEvent','contact-person-saved');
    } else {
      handleErrorResponse(response.data.error);
    }
  } catch (error) {
    handleErrorResponse(error);
  }
};

const handleErrorResponse = (error) => {
  spinner.value = false;
  buttonDisable.value = false;
  let errorsHtml = '';
  if (typeof error === 'string') {
    errorsHtml = error;
  } else {
    Object.values(error).forEach((err) => {
      errorsHtml += `${err}\n`;
    });
  }
  toast.error('Error! '+errorsHtml);
};

onMounted(() => {
  eventBus.on('showCustomerSharedData', (contactPerson) => {
    contact_name.value = contactPerson.name;
    contact_email.value = contactPerson.email;
    contact_phone_number.value = contactPerson.phone_number;
    contact_login_enabled.value = contactPerson.login_enabled;
    contact_type.value = contactPerson.contact_type;
    contact_person_id.value = contactPerson.id;
    action_mode.value = 'edit';
    contact_username.value = contactPerson.username.split('-')[0];
  });

  eventBus.on('addNewConstactPersonEvent', () => {
    formReset();
    action_mode.value = 'add';
  });
});
</script>
